import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaUserPlus, 
  FaSignInAlt, 
  FaLock, 
  FaEnvelope, 
  FaCheckCircle,
  FaRocket,
  FaShieldAlt,
  FaUsers,
  FaChartLine,
  FaMobileAlt,
  FaDesktop,
  FaTabletAlt,
  FaPlay,
  FaHeadset,
  FaBook,
  FaClock,
  FaCog,
  FaBell,
  FaWallet,
  FaExchangeAlt,
  FaFileAlt,
  FaEye,
  FaEyeSlash,
  FaFingerprint
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const GetStarted = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);

  const steps = [
    {
      icon: <FaUserPlus className="text-4xl" />,
      title: "Create Account",
      description: "Sign up for a BT India account in just a few minutes",
      details: [
        "Fill in your personal information",
        "Verify your email address",
        "Complete KYC verification",
        "Set up your security preferences"
      ],
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: <FaSignInAlt className="text-4xl" />,
      title: "Login & Access",
      description: "Access your dashboard with secure authentication",
      details: [
        "Use your email and password",
        "Enable two-factor authentication",
        "Access from any device",
        "Stay logged in securely"
      ],
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50"
    },
    {
      icon: <FaCog className="text-4xl" />,
      title: "Setup Profile",
      description: "Customize your profile and preferences",
      details: [
        "Upload profile picture",
        "Set notification preferences",
        "Configure security settings",
        "Add payment methods"
      ],
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      icon: <FaRocket className="text-4xl" />,
      title: "Start Using",
      description: "Begin managing your finances with BT India",
      details: [
        "Explore dashboard features",
        "Make your first transaction",
        "Set up recurring payments",
        "Monitor your activity"
      ],
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50"
    }
  ];

  const features = [
    {
      icon: <FaWallet className="text-3xl" />,
      title: "Wallet Management",
      description: "Manage your digital wallet, view balance, and track transactions in real-time."
    },
    {
      icon: <FaExchangeAlt className="text-3xl" />,
      title: "Money Transfers",
      description: "Send money instantly to other users or bank accounts with competitive fees."
    },
    {
      icon: <FaChartLine className="text-3xl" />,
      title: "Analytics & Reports",
      description: "View detailed analytics, spending patterns, and generate financial reports."
    },
    {
      icon: <FaBell className="text-3xl" />,
      title: "Notifications",
      description: "Get real-time notifications for transactions, security alerts, and updates."
    }
  ];

  const platforms = [
    {
      icon: <FaDesktop className="text-4xl" />,
      title: "Web Platform",
      description: "Access BT India from any web browser with full functionality.",
      features: ["Responsive Design", "Real-time Updates", "Full Feature Access"],
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      icon: <FaMobileAlt className="text-4xl" />,
      title: "Mobile Apps",
      description: "Download our mobile apps for Android and iOS devices.",
      features: ["Native Performance", "Offline Mode", "Push Notifications"],
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50"
    },
    {
      icon: <FaTabletAlt className="text-4xl" />,
      title: "Tablet Support",
      description: "Optimized experience for tablet devices with touch-friendly interface.",
      features: ["Touch Optimized", "Large Screen Layout", "Portrait/Landscape"],
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50"
    }
  ];

  const tutorials = [
    {
      title: "Getting Started Guide",
      duration: "5 min",
      description: "Complete walkthrough of setting up your account and basic features.",
      icon: <FaPlay className="text-2xl" />,
      type: "video"
    },
    {
      title: "Security Best Practices",
      duration: "3 min",
      description: "Learn how to keep your account secure and protect your data.",
      icon: <FaShieldAlt className="text-2xl" />,
      type: "guide"
    },
    {
      title: "Making Your First Transaction",
      duration: "2 min",
      description: "Step-by-step guide to sending money and managing transfers.",
      icon: <FaExchangeAlt className="text-2xl" />,
      type: "tutorial"
    },
    {
      title: "Understanding Your Dashboard",
      duration: "4 min",
      description: "Learn to navigate and use all dashboard features effectively.",
      icon: <FaChartLine className="text-2xl" />,
      type: "video"
    }
  ];

  const tabs = [
    { id: 'login', label: 'Login Guide', icon: <FaSignInAlt /> },
    { id: 'register', label: 'Registration', icon: <FaUserPlus /> },
    { id: 'features', label: 'Features', icon: <FaCog /> },
    { id: 'tutorials', label: 'Tutorials', icon: <FaPlay /> }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'login':
        return (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                How to Login
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Follow these simple steps to access your BT India account securely.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              <div className="space-y-6 sm:space-y-8">
                <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-xl border border-gray-100">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Login Steps</h3>
                  <div className="space-y-4 sm:space-y-6">
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className="bg-blue-100 text-blue-600 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 mt-1">
                        1
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Visit Login Page</h4>
                        <p className="text-gray-600 text-sm sm:text-base">Go to the login page and enter your registered email address.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className="bg-blue-100 text-blue-600 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 mt-1">
                        2
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Enter Password</h4>
                        <p className="text-gray-600 text-sm sm:text-base">Enter your secure password. Use biometric authentication if enabled.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className="bg-blue-100 text-blue-600 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 mt-1">
                        3
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Two-Factor Authentication</h4>
                        <p className="text-gray-600 text-sm sm:text-base">Complete 2FA verification if enabled for enhanced security.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      <div className="bg-blue-100 text-blue-600 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 mt-1">
                        4
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Access Dashboard</h4>
                        <p className="text-gray-600 text-sm sm:text-base">You're now logged in! Explore your personalized dashboard.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-2xl">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <FaShieldAlt className="text-green-600 mr-2" />
                    Security Tips
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                      Never share your login credentials
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                      Enable two-factor authentication
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                      Use a strong, unique password
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                      Log out from shared devices
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-xl border border-gray-100">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Login Form</h3>
                <form className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        className="w-full pl-10 pr-12 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="ml-2 text-sm text-gray-600">Remember me</span>
                    </label>
                    <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-800">
                      Forgot Password?
                    </Link>
                  </div>
                  <Link
                    to="/login"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 sm:py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 inline-flex items-center justify-center text-sm sm:text-base"
                  >
                    <FaSignInAlt className="mr-2" />
                    Login Now
                  </Link>
                </form>
                <div className="mt-4 sm:mt-6 text-center">
                  <p className="text-gray-600 text-sm sm:text-base">
                    Don't have an account?{' '}
                    <Link to="/register" className="text-blue-600 hover:text-blue-800 font-semibold">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'register':
        return (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Create Your Account
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join thousands of users who trust BT India for their financial management needs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`${step.bgColor} p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-300`}
                >
                  <div className={`bg-gradient-to-r ${step.color} text-white p-6 rounded-2xl mb-6 flex justify-center`}>
                    {step.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600 mb-6">{step.description}</p>
                  <ul className="space-y-3">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <FaCheckCircle className="text-green-500 mr-3 text-sm" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-12 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 inline-flex items-center text-lg"
              >
                <FaUserPlus className="mr-2" />
                Start Registration
              </Link>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Platform Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover the powerful features that make BT India the preferred choice for digital financial management.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-xl mb-6 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-3xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Multi-Platform Access
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {platforms.map((platform, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`${platform.bgColor} p-6 rounded-2xl border border-gray-100`}
                  >
                    <div className={`bg-gradient-to-r ${platform.color} text-white p-4 rounded-xl mb-4 flex justify-center`}>
                      {platform.icon}
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">{platform.title}</h4>
                    <p className="text-gray-600 mb-4">{platform.description}</p>
                    <ul className="space-y-2">
                      {platform.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700">
                          <FaCheckCircle className="text-green-500 mr-2 text-xs" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'tutorials':
        return (
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Video Tutorials & Guides
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Learn how to use BT India effectively with our comprehensive tutorials and guides.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {tutorials.map((tutorial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 rounded-xl">
                      {tutorial.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{tutorial.title}</h3>
                        <span className="text-sm text-gray-500 flex items-center">
                          <FaClock className="mr-1" />
                          {tutorial.duration}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4">{tutorial.description}</p>
                      <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm">
                        Watch Now
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-12 rounded-3xl text-center">
              <h3 className="text-3xl font-bold mb-6">Need More Help?</h3>
              <p className="text-xl mb-8 text-blue-100">
                Our support team is available 24/7 to assist you with any questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/support"
                  className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 inline-flex items-center"
                >
                  <FaHeadset className="mr-2" />
                  Contact Support
                </Link>
                <a
                  href="#documentation"
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 inline-flex items-center"
                >
                  <FaBook className="mr-2" />
                  Documentation
                </a>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8 }}
        className="bg-white/90 backdrop-blur-xl shadow-lg sticky top-0 z-50 border-b border-white/20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <FaRocket className="text-white text-xl" />
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                BTIndia
              </h1>
            </Link>
            <Link
              to="/"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-xl transition-all duration-300 font-medium shadow-lg"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Get Started with BT India
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-4xl mx-auto">
              Your complete guide to setting up and using BT India's digital financial platform.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full">
                <FaUserPlus className="text-green-400" />
                <span>Quick Setup</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full">
                <FaShieldAlt className="text-green-400" />
                <span>Secure Access</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full">
                <FaRocket className="text-green-400" />
                <span>Easy to Use</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-6 sm:py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 text-sm sm:text-base ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GetStarted; 